import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVELS = [
  "High School",
  "Travel Ball",
  "JuCo",
  "NCAA D1 / D2 / D3",
  "NAIA",
  "Pro / Independent",
  "Other",
];

const INITIAL = {
  full_name: "",
  email: "",
  age: "",
  position: "",
  current_level: "",
  biggest_goal: "",
  current_schedule: "",
  injury_history: "",
  whats_holding_back: "",
  why_interested: "",
  start_timeline: "",
  decision_maker: "",
  current_program: "",
  ready_to_invest: "",
  phone: "",
};

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || String(e)).join(" ");
  return String(detail);
}

export default function ApplicationForm() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      await axios.post(`${API}/applications`, { ...form, age: Number(form.age) });
      setStatus("success");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
      setStatus("idle");
    }
  };

  return (
    <section id="apply" className="py-20 sm:py-28 lg:py-36 bg-[#141416] border-t border-zinc-800" data-testid="application-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader index="06" label="Application" title="Apply For 1-On-1 Coaching" />
        <Reveal delay={0.1}>
          <p className="text-sm sm:text-base text-zinc-400 leading-relaxed mb-12">
            Answer honestly. This is how I decide who gets a spot — and how I
            start building your program before we ever speak.
          </p>
        </Reveal>

        {status === "success" ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="border border-[#CCFF00]/40 bg-[#0B0B0C] p-10 sm:p-16 text-center"
            data-testid="application-success-message"
          >
            <CheckCircle2 className="w-14 h-14 text-[#CCFF00] mx-auto mb-6" />
            <h3 className="font-display uppercase text-4xl sm:text-5xl text-white tracking-wide mb-4">
              Application Submitted
            </h3>
            <p className="text-sm sm:text-base text-zinc-400 max-w-md mx-auto leading-relaxed">
              Jared will review your application and reach out by email if you're
              a good fit for the roster. Keep training hard in the meantime.
            </p>
          </motion.div>
        ) : (
          <Reveal delay={0.15}>
            <form onSubmit={handleSubmit} className="grid gap-6 sm:grid-cols-2" data-testid="application-form">
              <div>
                <label className="field-label" htmlFor="full_name">Full Name *</label>
                <input id="full_name" data-testid="input-full-name" required className="field-input"
                  placeholder="Your full name" value={form.full_name} onChange={set("full_name")} />
              </div>
              <div>
                <label className="field-label" htmlFor="email">Email *</label>
                <input id="email" data-testid="input-email" type="email" required className="field-input"
                  placeholder="you@email.com" value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label className="field-label" htmlFor="age">Age *</label>
                <input id="age" data-testid="input-age" type="number" min="10" max="60" required className="field-input"
                  placeholder="17" value={form.age} onChange={set("age")} />
              </div>
              <div>
                <label className="field-label" htmlFor="position">Primary Position *</label>
                <input id="position" data-testid="input-position" required className="field-input"
                  placeholder="e.g. SS, RHP, OF" value={form.position} onChange={set("position")} />
              </div>
              <div className="sm:col-span-2">
                <label className="field-label" htmlFor="current_level">Current Level *</label>
                <select id="current_level" data-testid="input-current-level" required className="field-input"
                  value={form.current_level} onChange={set("current_level")}>
                  <option value="" disabled>Select your current level</option>
                  {LEVELS.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="phone">Phone (optional)</label>
                <input id="phone" data-testid="input-phone" type="tel" className="field-input"
                  placeholder="For a faster response" value={form.phone} onChange={set("phone")} />
              </div>
              <div>
                <label className="field-label" htmlFor="start_timeline">How Soon Are You Looking To Start? *</label>
                <select id="start_timeline" data-testid="input-start-timeline" required className="field-input"
                  value={form.start_timeline} onChange={set("start_timeline")}>
                  <option value="" disabled>Select one</option>
                  <option value="Immediately">Immediately</option>
                  <option value="Within 30 days">Within 30 days</option>
                  <option value="Just researching">Just researching</option>
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="decision_maker">Who Helps Make This Decision? *</label>
                <select id="decision_maker" data-testid="input-decision-maker" required className="field-input"
                  value={form.decision_maker} onChange={set("decision_maker")}>
                  <option value="" disabled>Select one</option>
                  <option value="Just me">Just me</option>
                  <option value="Me and my parents">Me and my parents</option>
                  <option value="My parents">My parents</option>
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="ready_to_invest">Ready To Invest If It's The Right Fit? *</label>
                <select id="ready_to_invest" data-testid="input-ready-to-invest" required className="field-input"
                  value={form.ready_to_invest} onChange={set("ready_to_invest")}>
                  <option value="" disabled>Select one</option>
                  <option value="Yes — ready now">Yes — ready now</option>
                  <option value="Need to discuss with my parents first">Need to discuss with my parents first</option>
                  <option value="Not right now">Not right now</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="field-label" htmlFor="current_program">Are You Currently Following Another Program Or Coach? *</label>
                <input id="current_program" data-testid="input-current-program" required className="field-input"
                  placeholder="e.g. Team lifts only, online program, nothing structured" value={form.current_program} onChange={set("current_program")} />
              </div>
              <div className="sm:col-span-2">
                <label className="field-label" htmlFor="biggest_goal">Biggest Goal *</label>
                <textarea id="biggest_goal" data-testid="input-biggest-goal" required rows={3} className="field-input resize-none"
                  placeholder="What are you chasing? Be specific." value={form.biggest_goal} onChange={set("biggest_goal")} />
              </div>
              <div className="sm:col-span-2">
                <label className="field-label" htmlFor="current_schedule">Current Training Schedule *</label>
                <textarea id="current_schedule" data-testid="input-current-schedule" required rows={3} className="field-input resize-none"
                  placeholder="What does your current week of training look like?" value={form.current_schedule} onChange={set("current_schedule")} />
              </div>
              <div className="sm:col-span-2">
                <label className="field-label" htmlFor="injury_history">Injury History (optional)</label>
                <textarea id="injury_history" data-testid="input-injury-history" rows={2} className="field-input resize-none"
                  placeholder="Any current or past injuries I should know about?" value={form.injury_history} onChange={set("injury_history")} />
              </div>
              <div className="sm:col-span-2">
                <label className="field-label" htmlFor="whats_holding_back">What's Holding You Back? *</label>
                <textarea id="whats_holding_back" data-testid="input-whats-holding-back" required rows={3} className="field-input resize-none"
                  placeholder="What do you feel is the biggest thing standing between you and the next level?" value={form.whats_holding_back} onChange={set("whats_holding_back")} />
              </div>
              <div className="sm:col-span-2">
                <label className="field-label" htmlFor="why_interested">Why 1-On-1 Coaching? *</label>
                <textarea id="why_interested" data-testid="input-why-interested" required rows={3} className="field-input resize-none"
                  placeholder="Why are you interested in working 1-on-1 instead of a generic program?" value={form.why_interested} onChange={set("why_interested")} />
              </div>

              {error && (
                <p className="sm:col-span-2 text-sm text-red-400 font-mono2" data-testid="application-error">
                  {error}
                </p>
              )}

              <div className="sm:col-span-2 pt-2">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  data-testid="submit-application-button"
                  className="w-full bg-[#CCFF00] text-[#0B0B0C] font-bold uppercase tracking-[0.15em] text-sm px-10 py-5 transition-all duration-300 hover:bg-white disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {status === "submitting" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Application"
                  )}
                </button>
                <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-mono2 text-center">
                  Every application is personally reviewed by Jared
                </p>
              </div>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
}
